import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { SettingsIconButton } from "../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { SelectOption } from "../../../components/newUI/select";
import { DotsSvg } from "../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../components/svg/EditPencilSvg";
import PlusSvg from "../../../components/svg/PlusSvg";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { Table } from "../../../components/table/Table";
import { RoleModel } from "../../../factories/role/role.model";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useAddRoleMutation,
  useAddUsersToRoleMutation,
  useDeleteRoleMutation,
  useGetRolesQuery,
  useRemoveUsersFromRoleMutation,
  useUpdateRoleMutation,
} from "../../../slices/RoleSlice";
import { RoleModal } from "../components/RoleModal";
import { TeamLayout } from "../team/TeamLayout";
import { TeamTabs } from "../team/TeamTabs";
import { useRoleUserOptions } from "./useRoleUserOptions";

const getCreatedByName = (role: RoleModel) =>
  [
    role.createdByUser?.userProfile?.firstName,
    role.createdByUser?.userProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ") ||
  role.createdByUser?.userProfile?.email ||
  "—";

/** Up to 4 members' initials (their own colour), then "+N". */
const RoleMembers = ({
  userIds,
  userOptions,
  count,
}: {
  userIds: string[];
  userOptions: SelectOption[];
  count: number;
}) => {
  const people = userIds
    .map((id) => userOptions.find((option) => option.value === id))
    .filter((option): option is SelectOption => !!option);
  if (people.length === 0)
    return <span className={"text-[#8A90A2]"}>{count ? count : "—"}</span>;
  const shown = people.slice(0, 4);
  return (
    <div className={"flex items-center"}>
      {shown.map((person, index) => (
        <span
          key={String(person.value)}
          title={person.label}
          style={{ backgroundColor: person.avatar?.color || "#1E3A8A" }}
          className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white ${
            index ? "-ml-1" : ""
          }`}>
          {person.avatar?.label}
        </span>
      ))}
      {people.length > shown.length && (
        <span className={"ml-1.5 text-xs font-medium text-[#8A90A2]"}>
          +{people.length - shown.length}
        </span>
      )}
    </div>
  );
};

export const Roles = () => {
  const orgId = getLinkedFirm()?.orgId;
  const user = useAppSelector((state) => state.appReducer.user);

  const [selectedEditRole, setSelectedEditRole] = useState<RoleModel | null>(
    null,
  );
  const [selectedDeleteRole, setSelectedDeleteRole] =
    useState<RoleModel | null>(null);
  const [isOpenCreate, setIsOpenCreate] = useState(false);

  const { data, isLoading } = useGetRolesQuery(
    { orgId: orgId || "" },
    { skip: !orgId },
  );
  const { userOptions, isLoadingUsers } = useRoleUserOptions(orgId);
  const [addRole, { isLoading: isAddingRole }] = useAddRoleMutation();
  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeletingRole }] = useDeleteRoleMutation();
  const [addUsersToRole, { isLoading: isAddingUsersToRole }] =
    useAddUsersToRoleMutation();
  const [removeUsersFromRole, { isLoading: isRemovingUsersFromRole }] =
    useRemoveUsersFromRoleMutation();

  const selectedEditRoleUsers = useMemo<SelectOption[]>(() => {
    if (!selectedEditRole) {
      return [];
    }

    const assignedUserIds = new Set(
      selectedEditRole.userRoles.map((userRole) => userRole.userId),
    );

    return userOptions.filter((userOption) =>
      assignedUserIds.has(userOption.value),
    );
  }, [selectedEditRole, userOptions]);

  const handleEditRole = useCallback((role: RoleModel) => {
    setSelectedEditRole(role);
  }, []);

  const handleDeleteRole = useCallback((role: RoleModel) => {
    setSelectedDeleteRole(role);
  }, []);

  const handleCreateRole = async (
    roleName: string,
    selectedUserIds: string[],
  ) => {
    if (!orgId || !user?.id) {
      return;
    }

    try {
      await addRole({
        roleName,
        orgId,
        createdByUserId: user.id,
        userIds: selectedUserIds,
      }).unwrap();
      toast("Role successfully created", { type: "success" });
      setIsOpenCreate(false);
    } catch {
      toast("Something went wrong", { type: "error" });
    }
  };

  const handleUpdateRole = async (
    roleName: string,
    selectedUserIds: string[],
  ) => {
    if (!selectedEditRole) {
      return;
    }

    try {
      const existingUserRolesByUserId = new Map(
        selectedEditRole.userRoles.map((userRole) => [
          userRole.userId,
          userRole,
        ]),
      );
      const selectedUserIdSet = new Set(selectedUserIds);
      const usersToAdd = selectedUserIds
        .filter((userId) => !existingUserRolesByUserId.has(userId))
        .map((userId) => ({ roleId: selectedEditRole.id, userId }));
      const userRoleIdsToRemove = selectedEditRole.userRoles
        .filter((userRole) => !selectedUserIdSet.has(userRole.userId))
        .map((userRole) => userRole.id);

      const requests: Promise<unknown>[] = [
        updateRole({ ...selectedEditRole, roleName }).unwrap(),
      ];

      if (usersToAdd.length) {
        requests.push(addUsersToRole(usersToAdd).unwrap());
      }

      if (userRoleIdsToRemove.length) {
        requests.push(removeUsersFromRole(userRoleIdsToRemove).unwrap());
      }

      await Promise.all(requests);
      toast("Role successfully updated", { type: "success" });
      setSelectedEditRole(null);
    } catch {
      toast("Something went wrong", { type: "error" });
    }
  };

  const handleConfirmDeleteRole = async () => {
    if (!selectedDeleteRole) {
      return;
    }

    try {
      await deleteRole({ roleId: selectedDeleteRole.id }).unwrap();
      toast("Role successfully deleted", { type: "success" });
      setSelectedDeleteRole(null);
    } catch {
      toast("Something went wrong", { type: "error" });
    }
  };

  const roleColumns = useMemo<ColumnDef<RoleModel>[]>(
    () => [
      {
        accessorKey: "roleName",
        header: "Name",
        sortingFn: "alphanumeric",
        meta: {
          className: "text-[#1D2C3B] font-semibold py-2",
        },
      },
      {
        accessorKey: "createdAt",
        header: "Date Created",
        sortingFn: "alphanumeric",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <span>{value ? moment(value).format("DD MMM YYYY") : "—"}</span>
          );
        },
        meta: {
          className: "text-[#1D2C3B] font-semibold py-2",
        },
      },
      {
        id: "createdBy",
        header: "Created By",
        accessorFn: (row) => getCreatedByName(row),
        sortingFn: "alphanumeric",
        meta: {
          className: "text-[#1D2C3B] font-semibold py-2",
        },
      },
      {
        id: "members",
        header: "Members",
        accessorFn: (row) => row.appliedToUsersCounts,
        sortingFn: "basic",
        cell: ({ row }) => (
          <RoleMembers
            userIds={row.original.userRoles.map((userRole) => userRole.userId)}
            userOptions={userOptions}
            count={row.original.appliedToUsersCounts}
          />
        ),
        meta: { className: "py-2" },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={"flex justify-end"}>
            <SettingsDropdown
              actionsList={[
                {
                  name: "Edit",
                  action: () => void handleEditRole(row.original),
                  icon: <EditPencilSvg />,
                },

                {
                  name: "Delete",
                  action: () => handleDeleteRole(row.original),
                  icon: <TrashSvg />,
                  extraClasses: "text-[#DC2626]",
                  iconColor: "text-[#DC2626]",
                },
              ]}>
              <SettingsIconButton
                icon={<DotsSvg />}
                onClick={() => null}
                size={"small"}
              />
            </SettingsDropdown>
          </div>
        ),
        meta: { className: "py-2" },
      },
    ],
    [handleDeleteRole, handleEditRole, userOptions],
  );

  return (
    <>
      {/* Roles live inside Team since 2026-10-25 (owner): Members · Invitations · Roles. */}
      <TeamLayout>
        <SettingsCard extraClasses={"p-4"}>
          <div className={"mb-4 flex justify-between gap-4"}>
            <TeamTabs activeTab={"roles"} />

            <button
              onClick={() => setIsOpenCreate(true)}
              className={
                "flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-[#6938EF] px-4 text-sm font-medium text-white"
              }>
              <PlusSvg />
              <span>Create Role</span>
            </button>
          </div>

          <div>
            {isLoading ? (
              <LottieLoading />
            ) : (
              <Table
                data={data || []}
                columns={roleColumns}
                initialSorting={[{ id: "roleName", desc: false }]}
              />
            )}
          </div>
        </SettingsCard>
      </TeamLayout>
      {!!selectedEditRole && (
        <RoleModal
          onClose={() => setSelectedEditRole(null)}
          defaultValue={selectedEditRole.roleName}
          userOptions={userOptions}
          defaultSelectedUsers={selectedEditRoleUsers}
          isLoading={
            isUpdatingRole ||
            isLoadingUsers ||
            isAddingUsersToRole ||
            isRemovingUsersFromRole
          }
          onSave={(roleName, selectedUserIds) =>
            void handleUpdateRole(roleName, selectedUserIds)
          }
        />
      )}
      {isOpenCreate && (
        <RoleModal
          onClose={() => setIsOpenCreate(false)}
          userOptions={userOptions}
          isLoading={isAddingRole || isLoadingUsers}
          onSave={(roleName, selectedUserIds) =>
            void handleCreateRole(roleName, selectedUserIds)
          }
        />
      )}
      {selectedDeleteRole && (
        <SettingsConfirmModal
          onClose={() => setSelectedDeleteRole(null)}
          onConfirm={() => void handleConfirmDeleteRole()}
          isLoading={isDeletingRole}
          icon={<TrashSvg />}
          title={`Delete '${selectedDeleteRole.roleName}' role?`}
          description={"Do you want to permanently delete this role?"}
          confirmText={"Delete"}
          variant={"danger"}
        />
      )}
    </>
  );
};
